import { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    Plus,
    Trash2,
    Power,
    PowerOff,
    X,
    Copy,
    Wallet,
    Search,
    ChevronLeft,
    ChevronRight,
    Loader2,
    Target,
    UserPlus,
    UserMinus,
    Lock,
    RotateCcw,
    Sparkles,
    KeyRound,
    Mail,
} from "lucide-react";
import api from "../../lib/api";
import ThemedSelect from "../../components/ThemedSelect";
import { useConfirm } from "../../components/ConfirmDialog";
import AccessOverridesModal from "../../components/AccessOverridesModal";

const ROLE_OPTIONS = [
    { value: "learner", label: "Apprenant" },
    { value: "manager", label: "Manager" },
    { value: "admin", label: "Admin" },
];

const ROLE_OPTIONS_COMPACT = [
    { value: "learner", label: "learner" },
    { value: "manager", label: "manager" },
    { value: "admin", label: "admin" },
];

const PAGE_SIZE = 50;

export default function AdminUsers() {
    const confirm = useConfirm();
    const [users, setUsers] = useState([]);
    const [meta, setMeta] = useState({ total: 0, page: 1, page_size: PAGE_SIZE, counts: { by_role: {}, by_status: {}, by_cash: {} } });
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({
        email: "",
        name: "",
        role: "learner",
        password: "",
    });
    const [createdInfo, setCreatedInfo] = useState(null);

    // Iter-27.15: search, filters, pagination, bulk selection
    const [searchInput, setSearchInput] = useState("");
    const [searchQ, setSearchQ] = useState("");  // debounced
    const [filterRole, setFilterRole] = useState("all");
    const [filterStatus, setFilterStatus] = useState("all");
    const [filterCash, setFilterCash] = useState("all");
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    // Iter-27.26: bulk assign-to-mission
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    // Iter-27.55 — User dont on gère les overrides (null = modal fermée)
    const [overridesUser, setOverridesUser] = useState(null);
    const debounceRef = useRef(null);

    // Debounce the search input → searchQ (server-side)
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSearchQ(searchInput.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(debounceRef.current);
    }, [searchInput]);

    const reload = async () => {
        setLoading(true);
        try {
            const params = { page, page_size: PAGE_SIZE };
            if (searchQ) params.q = searchQ;
            if (filterRole !== "all") params.role = filterRole;
            if (filterStatus !== "all") params.status = filterStatus;
            if (filterCash !== "all") params.cash = filterCash;
            const r = await api.get("/admin/users", { params });
            setUsers(r.data.items || []);
            setMeta({
                total: r.data.total || 0,
                page: r.data.page || 1,
                page_size: r.data.page_size || PAGE_SIZE,
                counts: r.data.counts || { by_role: {}, by_status: {}, by_cash: {} },
            });
        } catch {
            toast.error("Erreur de chargement");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQ, filterRole, filterStatus, filterCash, page]);

    // Auto-reset page when filters change (covered by useEffect above resetting via setPage(1) on debounce)
    useEffect(() => {
        setPage(1);
    }, [filterRole, filterStatus, filterCash]);

    // Selection helpers
    const toggleSelect = (uid) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(uid)) next.delete(uid);
            else next.add(uid);
            return next;
        });
    };
    const allOnPageSelected = users.length > 0 && users.every((u) => selected.has(u.user_id));
    const toggleSelectAllOnPage = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allOnPageSelected) {
                users.forEach((u) => next.delete(u.user_id));
            } else {
                users.forEach((u) => next.add(u.user_id));
            }
            return next;
        });
    };
    const clearSelection = () => setSelected(new Set());

    const totalPages = Math.max(1, Math.ceil(meta.total / meta.page_size));

    const create = async () => {
        if (!form.email || !form.name) {
            toast.error("Email et nom requis");
            return;
        }
        try {
            const { data } = await api.post("/admin/users", form);
            toast.success("Utilisateur créé");
            setCreatedInfo(data);
            setForm({ email: "", name: "", role: "learner", password: "" });
            setCreating(false);
            reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    };

    const updateUser = async (uid, patch) => {
        try {
            await api.patch(`/admin/users/${uid}`, patch);
            toast.success("Mis à jour");
            reload();
        } catch {
            toast.error("Erreur");
        }
    };

    const toggleCashEligible = async (u) => {
        const willEnable = !u.cash_eligible;
        if (willEnable) {
            const ok = await confirm({
                title: `Activer la rémunération en € pour ${u.name} ?`,
                description:
                    "Cet apprenant verra apparaître la page « Mes paiements » dans son menu et pourra réclamer ses revenus mensuels. Tu pourras désactiver à tout moment.",
                confirmLabel: "Activer",
            });
            if (!ok) return;
        }
        try {
            await api.put(`/admin/users/${u.user_id}/cash-eligible`, {
                eligible: willEnable,
            });
            toast.success(willEnable ? "Rémunération activée" : "Rémunération désactivée");
            reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    };

    // Iter-27.15: bulk action runner
    const runBulk = async (action, label, requireConfirm = false) => {
        const ids = Array.from(selected);
        if (ids.length === 0) {
            toast.error("Sélectionne au moins un utilisateur");
            return;
        }
        if (requireConfirm) {
            const ok = await confirm({
                title: `${label} pour ${ids.length} utilisateur${ids.length > 1 ? "s" : ""} ?`,
                description:
                    action === "delete"
                        ? "Cette action est IRRÉVERSIBLE. Toutes les données associées seront supprimées définitivement."
                        : `Tu vas appliquer « ${label} » à ${ids.length} compte${ids.length > 1 ? "s" : ""}. Tu peux toujours annuler après.`,
                confirmLabel: action === "delete" ? "Tout supprimer" : "Confirmer",
                destructive: action === "delete",
            });
            if (!ok) return;
        }
        setBulkBusy(true);
        try {
            const r = await api.post("/admin/users/bulk", {
                user_ids: ids,
                action,
            });
            const skipped = (r.data.skipped || []).length;
            const affected = r.data.affected || 0;
            let msg = `${label} appliqué·e à ${affected} compte${affected > 1 ? "s" : ""}`;
            if (skipped) msg += ` • ${skipped} ignoré${skipped > 1 ? "s" : ""}`;
            toast.success(msg);
            clearSelection();
            await reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setBulkBusy(false);
        }
    };

    const filterPills = useMemo(
        () => [
            // Role
            { id: "role-all", group: "Rôle", label: "Tous", value: "all", current: filterRole, set: setFilterRole, count: meta.total },
            { id: "role-learner", group: "Rôle", label: "Apprenants", value: "learner", current: filterRole, set: setFilterRole, count: meta.counts.by_role?.learner || 0 },
            { id: "role-manager", group: "Rôle", label: "Managers", value: "manager", current: filterRole, set: setFilterRole, count: meta.counts.by_role?.manager || 0 },
            { id: "role-admin", group: "Rôle", label: "Admins", value: "admin", current: filterRole, set: setFilterRole, count: meta.counts.by_role?.admin || 0 },
            { id: "role-candidate", group: "Rôle", label: "Candidats", value: "candidate", current: filterRole, set: setFilterRole, count: meta.counts.by_role?.candidate || 0 },
        ],
        [filterRole, meta.counts, meta.total],
    );

    const deleteUser = async (uid) => {
        const target = users.find((u) => u.user_id === uid);
        const ok = await confirm({
            title: "Supprimer définitivement cet utilisateur ?",
            description: target
                ? `${target.name} (${target.email}) sera supprimé·e ainsi que toute sa progression, ses badges et ses commentaires. Cette action est irréversible.`
                : "Cette action est irréversible.",
            confirmLabel: "Supprimer",
            tone: "danger",
        });
        if (!ok) return;
        try {
            await api.delete(`/admin/users/${uid}`);
            toast.success("Utilisateur supprimé");
            reload();
        } catch {
            toast.error("Suppression impossible");
        }
    };

    // Iter-30 — Force un utilisateur à refaire l'onboarding en 1 clic
    const resetOnboarding = async (uid) => {
        const target = users.find((u) => u.user_id === uid);
        const ok = await confirm({
            title: "Forcer le re-onboarding ?",
            description: target
                ? `${target.name} (${target.email}) devra refaire le parcours d'onboarding complet (vidéo de bienvenue, message Shariti, quiz fondamental) à sa prochaine connexion. Sa progression et ses points sont conservés.`
                : "L'utilisateur devra refaire le parcours d'onboarding.",
            confirmLabel: "Forcer le re-onboarding",
        });
        if (!ok) return;
        try {
            await api.post(`/admin/users/${uid}/reset-onboarding`);
            toast.success("Re-onboarding forcé — l'utilisateur le verra à sa prochaine connexion");
            reload();
        } catch {
            toast.error("Impossible de réinitialiser l'onboarding");
        }
    };

    const copyPassword = (pwd) => {
        navigator.clipboard.writeText(pwd);
        toast.success("Mot de passe copié");
    };

    // Iter-37 — Réinitialisation du mot de passe par l'admin
    const [resetTarget, setResetTarget] = useState(null);
    const [resetSendEmail, setResetSendEmail] = useState(true);
    const [resetLoading, setResetLoading] = useState(false);
    const [resetInfo, setResetInfo] = useState(null);

    const doResetPassword = async () => {
        setResetLoading(true);
        try {
            const { data } = await api.post(
                `/admin/users/${resetTarget.user_id}/reset-password`,
                { send_email: resetSendEmail },
            );
            setResetInfo({ ...data, name: resetTarget.name });
            setResetTarget(null);
            if (data.email_sent) toast.success(`Identifiants envoyés à ${data.email} ✉️`);
            else toast.success("Nouveau mot de passe généré");
            if (data.email_error) toast.warning(`Email non envoyé : ${data.email_error}`);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Impossible de réinitialiser le mot de passe");
        } finally {
            setResetLoading(false);
        }
    };

    const resendCredentials = async () => {
        try {
            const { data } = await api.post(
                `/admin/users/${resetInfo.user_id}/reset-password`,
                { password: resetInfo.temp_password, send_email: true },
            );
            if (data.email_sent) {
                setResetInfo((prev) => ({ ...prev, email_sent: true }));
                toast.success(`Identifiants envoyés à ${data.email} ✉️`);
            } else {
                toast.error(data.email_error || "Envoi impossible");
            }
        } catch {
            toast.error("Envoi impossible");
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-1">
                        Utilisateurs
                    </p>
                    <h2 className="text-2xl font-extrabold text-white">
                        {meta.total} membre{meta.total > 1 ? "s" : ""}
                    </h2>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Iter-30.25 — Bouton 1-clic pour rafraîchir les OG images
                        des actualités qui n'en ont pas. Utile après l'import de
                        news historiques ou si un OG fetch a échoué (CORS, 404). */}
                    <button
                        onClick={async () => {
                            try {
                                const { data } = await api.post("/admin/news/refresh-og-images");
                                toast.success(data.message);
                            } catch (e) {
                                toast.error("Échec du rafraîchissement");
                            }
                        }}
                        data-testid="admin-refresh-og-images-button"
                        className="inline-flex items-center gap-2 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 font-bold py-2 px-3 rounded-xl text-xs border border-emerald-400/30 transition-all"
                        title="Re-télécharge les images d'aperçu des actualités qui n'en ont pas"
                    >
                        <Sparkles className="w-3.5 h-3.5" />
                        Récupérer images actus
                    </button>
                    {/* Iter-30.19 — Bouton 1-clic pour purger les actualités orphelines
                        (dont l'association a été supprimée → news affichées sans nom
                        d'asso sur le dashboard ambassadeur). */}
                    <button
                        onClick={async () => {
                            const ok = await confirm({
                                title: "Purger les actualités orphelines",
                                description:
                                    "Supprime définitivement toutes les actualités dont l'association n'existe plus (anciens tests, asso supprimées). Action irréversible.",
                                confirmText: "Tout nettoyer",
                                tone: "danger",
                            });
                            if (!ok) return;
                            try {
                                const { data } = await api.post("/admin/news/purge-orphans");
                                toast.success(data.message || `${data.purged_count} actualité(s) supprimée(s).`);
                            } catch (e) {
                                toast.error("Échec du nettoyage — réessaie");
                            }
                        }}
                        data-testid="admin-purge-orphan-news-button"
                        className="inline-flex items-center gap-2 bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 font-bold py-2 px-3 rounded-xl text-xs border border-amber-400/30 transition-all"
                        title="Supprime les actualités dont l'association a été supprimée"
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                        Purger actus orphelines
                    </button>
                    {/* Iter-30.17 — Bouton 1-clic pour purger TOUS les comptes démo.
                        Pas de blocklist : les emails restent libres pour ré-invitation
                        par de futurs vrais collaborateurs. La protection contre le
                        re-seed automatique est côté seed_data.py (détection prod). */}
                    <button
                        onClick={async () => {
                            const ok = await confirm({
                                title: "Purger les comptes démo",
                                description:
                                    "Supprime définitivement les 6 comptes démo (Sara, Yanis, Alex, Marie, Pierre, Léa) et toutes leurs données (points, badges, progression, paiements démo). Les emails restent libres pour ré-invitation. Action irréversible.",
                                confirmText: "Tout supprimer",
                                tone: "danger",
                            });
                            if (!ok) return;
                            try {
                                const { data } = await api.post("/admin/demo-users/purge");
                                toast.success(data.message || `${data.purged_count} compte(s) supprimé(s).`);
                                reload();
                            } catch (e) {
                                toast.error("Échec de la purge — réessaie");
                            }
                        }}
                        data-testid="admin-purge-demo-users-button"
                        className="inline-flex items-center gap-2 bg-red-500/15 text-red-300 hover:bg-red-500/25 font-bold py-2 px-3 rounded-xl text-xs border border-red-400/30 transition-all"
                        title="Supprime définitivement les 6 comptes démo seed. Les emails restent libres pour ré-invitation."
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                        Purger comptes démo
                    </button>
                    <button
                        onClick={() => {
                            setCreating(!creating);
                            setCreatedInfo(null);
                        }}
                        data-testid="admin-new-user-button"
                        className="inline-flex items-center gap-2 bg-shariti-orange text-white font-extrabold py-2.5 px-4 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all"
                    >
                        <Plus className="w-4 h-4" />
                        Nouvel utilisateur
                    </button>
                </div>
            </div>

            {/* Search + filters bar */}
            <div className="space-y-3" data-testid="admin-users-toolbar">
                <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                    <input
                        type="search"
                        placeholder="Rechercher par nom ou email…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        data-testid="admin-users-search"
                        className="w-full pl-11 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-white/30 outline-none focus:border-shariti-orange/60 transition-all"
                    />
                </div>
                <div className="flex flex-wrap gap-1.5 items-center">
                    <span className="text-[10px] font-thin tracking-[0.25em] uppercase text-white/40 mr-1">
                        Rôle
                    </span>
                    {filterPills.map((p) => {
                        const active = p.current === p.value;
                        return (
                            <button
                                key={p.id}
                                onClick={() => p.set(p.value)}
                                data-testid={`admin-users-filter-${p.id}`}
                                className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-widest transition-all ${
                                    active
                                        ? "bg-shariti-orange text-white"
                                        : "bg-white/5 text-white/60 hover:bg-white/10"
                                }`}
                            >
                                {p.label}
                                {p.count !== undefined && (
                                    <span className="ml-1 opacity-70">({p.count})</span>
                                )}
                            </button>
                        );
                    })}
                </div>
                <div className="flex flex-wrap gap-1.5 items-center">
                    <span className="text-[10px] font-thin tracking-[0.25em] uppercase text-white/40 mr-1">
                        Statut
                    </span>
                    {[
                        { v: "all", l: "Tous" },
                        { v: "active", l: "Actifs", c: meta.counts.by_status?.active },
                        { v: "inactive", l: "Inactifs", c: meta.counts.by_status?.inactive },
                    ].map((o) => {
                        const active = filterStatus === o.v;
                        return (
                            <button
                                key={o.v}
                                onClick={() => setFilterStatus(o.v)}
                                data-testid={`admin-users-status-${o.v}`}
                                className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-widest transition-all ${
                                    active
                                        ? "bg-shariti-orange text-white"
                                        : "bg-white/5 text-white/60 hover:bg-white/10"
                                }`}
                            >
                                {o.l}
                                {o.c !== undefined && (
                                    <span className="ml-1 opacity-70">({o.c})</span>
                                )}
                            </button>
                        );
                    })}
                    <span className="text-[10px] font-thin tracking-[0.25em] uppercase text-white/40 ml-2 mr-1">
                        Rémunération
                    </span>
                    {[
                        { v: "all", l: "Toutes" },
                        { v: "eligible", l: "Cash actif", c: meta.counts.by_cash?.eligible },
                        { v: "not_eligible", l: "Cash inactif", c: meta.counts.by_cash?.not_eligible },
                    ].map((o) => {
                        const active = filterCash === o.v;
                        return (
                            <button
                                key={o.v}
                                onClick={() => setFilterCash(o.v)}
                                data-testid={`admin-users-cash-${o.v}`}
                                className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-widest transition-all ${
                                    active
                                        ? "bg-emerald-500/30 text-emerald-300 border border-emerald-500/40"
                                        : "bg-white/5 text-white/60 hover:bg-white/10"
                                }`}
                            >
                                {o.l}
                                {o.c !== undefined && (
                                    <span className="ml-1 opacity-70">({o.c})</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Bulk action banner */}
            <AnimatePresence>
                {selected.size > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className="sticky top-2 z-30 rounded-2xl p-3 bg-shariti-dark/95 backdrop-blur border border-shariti-orange/40 flex items-center justify-between gap-3 flex-wrap"
                        data-testid="admin-users-bulk-bar"
                    >
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-extrabold text-white">
                                {selected.size} sélectionné{selected.size > 1 ? "s" : ""}
                            </span>
                            <button
                                onClick={clearSelection}
                                className="text-[11px] text-white/60 hover:text-white underline-offset-4 hover:underline"
                            >
                                Tout désélectionner
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            <button
                                onClick={() => setAssignModalOpen(true)}
                                disabled={bulkBusy}
                                data-testid="admin-users-bulk-assign-mission"
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-shariti-orange/20 hover:bg-shariti-orange/30 text-shariti-orange-light text-xs font-bold disabled:opacity-50"
                            >
                                <Target className="w-3.5 h-3.5" /> Assigner à une mission
                            </button>
                            <button
                                onClick={() => runBulk("enable_cash", "Activer la rémunération", true)}
                                disabled={bulkBusy}
                                data-testid="admin-users-bulk-enable-cash"
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-xs font-bold disabled:opacity-50"
                            >
                                <Wallet className="w-3.5 h-3.5" /> Activer cash
                            </button>
                            <button
                                onClick={() => runBulk("disable_cash", "Désactiver la rémunération", true)}
                                disabled={bulkBusy}
                                data-testid="admin-users-bulk-disable-cash"
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 text-xs font-bold disabled:opacity-50"
                            >
                                Désactiver cash
                            </button>
                            <button
                                onClick={() => runBulk("activate", "Activer le compte")}
                                disabled={bulkBusy}
                                data-testid="admin-users-bulk-activate"
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-500/15 hover:bg-blue-500/25 text-blue-300 text-xs font-bold disabled:opacity-50"
                            >
                                <Power className="w-3.5 h-3.5" /> Activer
                            </button>
                            <button
                                onClick={() => runBulk("deactivate", "Désactiver le compte", true)}
                                disabled={bulkBusy}
                                data-testid="admin-users-bulk-deactivate"
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 text-xs font-bold disabled:opacity-50"
                            >
                                <PowerOff className="w-3.5 h-3.5" /> Désactiver
                            </button>
                            <button
                                onClick={() => runBulk("delete", "Supprimer définitivement", true)}
                                disabled={bulkBusy}
                                data-testid="admin-users-bulk-delete"
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 text-xs font-bold disabled:opacity-50"
                            >
                                {bulkBusy ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                    <Trash2 className="w-3.5 h-3.5" />
                                )}
                                Supprimer
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {createdInfo?.temp_password && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-3xl p-5 bg-shariti-orange/10 border border-shariti-orange/40"
                    data-testid="created-user-banner"
                >
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <p className="text-xs font-thin tracking-[0.25em] uppercase text-shariti-orange-light mb-1">
                                Identifiants à transmettre
                            </p>
                            <p className="font-bold text-white">{createdInfo.email}</p>
                            <p className="text-sm font-mono text-shariti-orange-light truncate">
                                {createdInfo.temp_password}
                            </p>
                        </div>
                        <div className="flex gap-1">
                            <button
                                onClick={() => copyPassword(createdInfo.temp_password)}
                                data-testid="copy-password-button"
                                className="w-9 h-9 rounded-xl bg-shariti-orange flex items-center justify-center hover:bg-shariti-orange-light"
                            >
                                <Copy className="w-4 h-4 text-white" />
                            </button>
                            <button
                                onClick={() => setCreatedInfo(null)}
                                className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10"
                            >
                                <X className="w-4 h-4 text-white/70" />
                            </button>
                        </div>
                    </div>
                </motion.div>
            )}

            {resetInfo?.temp_password && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-3xl p-5 bg-shariti-orange/10 border border-shariti-orange/40"
                    data-testid="reset-password-banner"
                >
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <p className="text-xs font-thin tracking-[0.25em] uppercase text-shariti-orange-light mb-1">
                                Nouveau mot de passe — {resetInfo.name}
                            </p>
                            <p className="font-bold text-white">{resetInfo.email}</p>
                            <p
                                className="text-sm font-mono text-shariti-orange-light truncate"
                                data-testid="reset-password-value"
                            >
                                {resetInfo.temp_password}
                            </p>
                            <p
                                className={`text-[11px] mt-1 ${
                                    resetInfo.email_sent ? "text-emerald-400" : "text-white/45"
                                }`}
                                data-testid="reset-password-email-status"
                            >
                                {resetInfo.email_sent
                                    ? "✉️ Identifiants envoyés par email au collaborateur"
                                    : "Email non envoyé — copie le mot de passe ou envoie-le avec le bouton ci-contre"}
                            </p>
                        </div>
                        <div className="flex gap-1">
                            <button
                                onClick={() => copyPassword(resetInfo.temp_password)}
                                data-testid="reset-password-copy"
                                title="Copier le mot de passe"
                                className="w-9 h-9 rounded-xl bg-shariti-orange flex items-center justify-center hover:bg-shariti-orange-light"
                            >
                                <Copy className="w-4 h-4 text-white" />
                            </button>
                            {!resetInfo.email_sent && (
                                <button
                                    onClick={resendCredentials}
                                    data-testid="reset-password-send-mail"
                                    title="Envoyer les identifiants par email"
                                    className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/20"
                                >
                                    <Mail className="w-4 h-4 text-white" />
                                </button>
                            )}
                            <button
                                onClick={() => setResetInfo(null)}
                                data-testid="reset-password-banner-close"
                                className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10"
                            >
                                <X className="w-4 h-4 text-white/70" />
                            </button>
                        </div>
                    </div>
                </motion.div>
            )}

            {resetTarget && (
                <div
                    className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
                    data-testid="reset-password-modal"
                >
                    <div className="glass-card rounded-3xl p-6 w-full max-w-md bg-[#141417]">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-xl bg-shariti-orange/15 flex items-center justify-center shrink-0">
                                <KeyRound className="w-5 h-5 text-shariti-orange" />
                            </div>
                            <h3 className="text-lg font-extrabold text-white">
                                Réinitialiser le mot de passe
                            </h3>
                        </div>
                        <p className="text-sm text-white/60 mb-4">
                            Un nouveau mot de passe temporaire sera généré pour{" "}
                            <span className="font-bold text-white">{resetTarget.name}</span>{" "}
                            ({resetTarget.email}). L'ancien mot de passe ne fonctionnera plus.
                        </p>
                        <label className="flex items-start gap-2.5 mb-5 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={resetSendEmail}
                                onChange={(e) => setResetSendEmail(e.target.checked)}
                                data-testid="reset-password-send-email-checkbox"
                                className="w-4 h-4 mt-0.5 accent-[#EE722E]"
                            />
                            <span className="text-sm text-white/70">
                                Envoyer directement les nouveaux identifiants par email au
                                collaborateur (mail brandé Shariti)
                            </span>
                        </label>
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={() => setResetTarget(null)}
                                data-testid="reset-password-cancel"
                                className="px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white/70 font-bold text-sm"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={doResetPassword}
                                disabled={resetLoading}
                                data-testid="reset-password-confirm"
                                className="px-4 py-2.5 rounded-xl bg-shariti-orange hover:bg-shariti-orange-light text-white font-extrabold text-sm flex items-center gap-2 disabled:opacity-60"
                            >
                                {resetLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                                Réinitialiser
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {creating && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass-card rounded-3xl p-5 space-y-3"
                    data-testid="admin-user-create-form"
                >
                    <input
                        type="email"
                        placeholder="email@shariti.fr"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="new-user-email"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-shariti-orange/60"
                    />
                    <input
                        type="text"
                        placeholder="Prénom Nom"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        data-testid="new-user-name"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-shariti-orange/60"
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <ThemedSelect
                            options={ROLE_OPTIONS}
                            value={form.role}
                            onChange={(v) => setForm({ ...form, role: v })}
                            dataTestid="new-user-role"
                        />
                        <input
                            type="text"
                            placeholder="Mot de passe (auto si vide)"
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            data-testid="new-user-password"
                            className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none"
                        />
                    </div>
                    <button
                        onClick={create}
                        data-testid="new-user-submit"
                        className="w-full bg-shariti-orange text-white font-extrabold py-3 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all"
                    >
                        Créer le compte
                    </button>
                </motion.div>
            )}

            <div className="glass-card rounded-3xl overflow-hidden" data-testid="admin-users-table">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-white/3">
                            <tr className="text-left text-xs font-thin tracking-widest text-white/50 uppercase">
                                <th className="p-4 w-10">
                                    <input
                                        type="checkbox"
                                        checked={allOnPageSelected}
                                        onChange={toggleSelectAllOnPage}
                                        data-testid="admin-users-select-all"
                                        aria-label="Tout sélectionner sur la page"
                                        className="accent-shariti-orange w-4 h-4 cursor-pointer"
                                    />
                                </th>
                                <th className="p-4">Utilisateur</th>
                                <th className="p-4 hidden sm:table-cell">Rôle</th>
                                <th className="p-4 hidden md:table-cell">Points</th>
                                <th className="p-4">Statut</th>
                                <th className="p-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {loading && users.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="p-10 text-center text-white/45 text-sm">
                                        <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                                        Chargement…
                                    </td>
                                </tr>
                            )}
                            {!loading && users.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="p-10 text-center text-white/45 text-sm">
                                        Aucun utilisateur ne correspond à ces filtres.
                                    </td>
                                </tr>
                            )}
                            {users.map((u) => (
                                <tr
                                    key={u.user_id}
                                    className={`hover:bg-white/3 ${
                                        selected.has(u.user_id) ? "bg-shariti-orange/10" : ""
                                    }`}
                                    data-testid={`admin-user-row-${u.user_id}`}
                                >
                                    <td className="p-4">
                                        <input
                                            type="checkbox"
                                            checked={selected.has(u.user_id)}
                                            onChange={() => toggleSelect(u.user_id)}
                                            data-testid={`admin-user-select-${u.user_id}`}
                                            aria-label={`Sélectionner ${u.name}`}
                                            className="accent-shariti-orange w-4 h-4 cursor-pointer"
                                        />
                                    </td>
                                    <td className="p-4">
                                        <div className="flex items-center gap-3">
                                            {u.avatar ? (
                                                <img
                                                    src={u.avatar}
                                                    alt=""
                                                    className="w-9 h-9 rounded-xl object-cover"
                                                />
                                            ) : (
                                                <div className="w-9 h-9 rounded-xl bg-shariti-orange/20 flex items-center justify-center font-bold text-shariti-orange">
                                                    {u.name?.[0]}
                                                </div>
                                            )}
                                            <div className="min-w-0">
                                                <p className="font-bold text-white truncate">
                                                    {u.name}
                                                </p>
                                                <p className="text-xs text-white/40 truncate">
                                                    {u.email}
                                                </p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-4 hidden sm:table-cell">
                                        <div className="w-32">
                                            <ThemedSelect
                                                options={ROLE_OPTIONS_COMPACT}
                                                value={u.role}
                                                onChange={(v) => updateUser(u.user_id, { role: v })}
                                                size="sm"
                                                dataTestid={`admin-role-select-${u.user_id}`}
                                            />
                                        </div>
                                    </td>
                                    <td className="p-4 hidden md:table-cell text-shariti-orange font-extrabold">
                                        {u.points || 0}
                                    </td>
                                    <td className="p-4">
                                        <span
                                            className={`text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 ${
                                                u.is_active === false
                                                    ? "bg-red-500/15 text-red-400 ring-1 ring-red-500/30"
                                                    : "bg-shariti-orange/20 text-shariti-orange-light"
                                            }`}
                                        >
                                            <span
                                                className={`w-1.5 h-1.5 rounded-full ${
                                                    u.is_active === false ? "bg-red-400" : "bg-shariti-orange"
                                                }`}
                                            />
                                            {u.is_active === false ? "Désactivé" : "Actif"}
                                        </span>
                                    </td>
                                    <td className="p-4 text-right">
                                        <div className="inline-flex gap-1">
                                            {u.role !== "admin" && u.role !== "candidate" && (
                                                <button
                                                    onClick={() => toggleCashEligible(u)}
                                                    data-testid={`admin-cash-toggle-${u.user_id}`}
                                                    className={`p-2 rounded-xl transition-colors ${
                                                        u.cash_eligible
                                                            ? "bg-emerald-500/20 hover:bg-emerald-500/30 ring-1 ring-emerald-500/40"
                                                            : "bg-white/5 hover:bg-emerald-500/15"
                                                    }`}
                                                    title={
                                                        u.cash_eligible
                                                            ? "Rémunération en € activée — clique pour désactiver"
                                                            : "Rémunération en € désactivée — clique pour activer"
                                                    }
                                                >
                                                    <Wallet
                                                        className={`w-4 h-4 ${
                                                            u.cash_eligible
                                                                ? "text-emerald-300"
                                                                : "text-white/50"
                                                        }`}
                                                    />
                                                </button>
                                            )}
                                            <button
                                                onClick={async () => {
                                                    const willDeactivate = u.is_active !== false;
                                                    if (willDeactivate) {
                                                        const ok = await confirm({
                                                            title: `Désactiver ${u.name} ?`,
                                                            description:
                                                                "Toutes ses sessions seront fermées immédiatement et il ne pourra plus se reconnecter tant que tu ne l'auras pas réactivé·e.",
                                                            confirmLabel: "Désactiver",
                                                            tone: "danger",
                                                        });
                                                        if (!ok) return;
                                                    }
                                                    updateUser(u.user_id, {
                                                        is_active: !willDeactivate,
                                                    });
                                                }}
                                                data-testid={`admin-toggle-${u.user_id}`}
                                                className={`p-2 rounded-xl transition-colors ${
                                                    u.is_active === false
                                                        ? "bg-red-500/20 hover:bg-red-500/30 ring-1 ring-red-500/40"
                                                        : "bg-white/5 hover:bg-shariti-orange/20"
                                                }`}
                                                title={
                                                    u.is_active === false
                                                        ? "Compte désactivé — clique pour réactiver"
                                                        : "Compte actif — clique pour désactiver"
                                                }
                                            >
                                                {u.is_active === false ? (
                                                    <PowerOff className="w-4 h-4 text-red-400" />
                                                ) : (
                                                    <Power className="w-4 h-4 text-white/70" />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setResetSendEmail(true);
                                                    setResetTarget(u);
                                                }}
                                                data-testid={`admin-reset-password-${u.user_id}`}
                                                title="Réinitialiser le mot de passe"
                                                className="p-2 rounded-xl bg-white/5 hover:bg-shariti-orange/20"
                                            >
                                                <KeyRound className="w-4 h-4 text-white/70" />
                                            </button>
                                            <button
                                                onClick={() => setOverridesUser(u)}
                                                data-testid={`admin-overrides-${u.user_id}`}
                                                title="Gérer le verrouillage personnalisé"
                                                className="p-2 rounded-xl bg-white/5 hover:bg-shariti-yellow/15"
                                            >
                                                <Lock className="w-4 h-4 text-white/70" />
                                            </button>
                                            <button
                                                onClick={() => resetOnboarding(u.user_id)}
                                                data-testid={`admin-reset-onboarding-${u.user_id}`}
                                                title="Forcer le re-onboarding au prochain login"
                                                className="p-2 rounded-xl bg-white/5 hover:bg-shariti-orange/20"
                                            >
                                                <RotateCcw className="w-4 h-4 text-white/70" />
                                            </button>
                                            <button
                                                onClick={() => deleteUser(u.user_id)}
                                                data-testid={`admin-delete-${u.user_id}`}
                                                className="p-2 rounded-xl bg-white/5 hover:bg-red-500/20"
                                            >
                                                <Trash2 className="w-4 h-4 text-white/70" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                    <div
                        className="flex items-center justify-between gap-3 px-4 py-3 border-t border-white/5 bg-white/[0.02]"
                        data-testid="admin-users-pagination"
                    >
                        <p className="text-xs text-white/55">
                            Page {meta.page} / {totalPages} ·{" "}
                            <span className="text-white">{meta.total}</span> utilisateur
                            {meta.total > 1 ? "s" : ""}
                        </p>
                        <div className="flex gap-1.5">
                            <button
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={meta.page <= 1 || loading}
                                data-testid="admin-users-page-prev"
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft className="w-3.5 h-3.5" /> Précédente
                            </button>
                            <button
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={meta.page >= totalPages || loading}
                                data-testid="admin-users-page-next"
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Suivante <ChevronRight className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Iter-27.26 — Bulk assign to mission modal */}
            <AnimatePresence>
                {assignModalOpen && (
                    <AssignToMissionModal
                        userIds={Array.from(selected)}
                        onClose={() => setAssignModalOpen(false)}
                        onDone={() => {
                            setAssignModalOpen(false);
                            clearSelection();
                        }}
                    />
                )}
                {/* Iter-27.55 — Modal d'overrides d'accès (verrouillage perso) */}
                {overridesUser && (
                    <AccessOverridesModal
                        userObj={overridesUser}
                        onClose={() => setOverridesUser(null)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}


function AssignToMissionModal({ userIds, onClose, onDone }) {
    const [assos, setAssos] = useState(null);
    const [selectedAid, setSelectedAid] = useState(null);
    const [mode, setMode] = useState("add"); // add | remove
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/admin/associations").then((r) => {
            const list = r.data || [];
            setAssos(list);
            if (list.length > 0) setSelectedAid(list[0].association_id);
        });
    }, []);

    const submit = async () => {
        if (!selectedAid || userIds.length === 0) return;
        setBusy(true);
        try {
            const body =
                mode === "add"
                    ? { add: userIds }
                    : { remove: userIds };
            const r = await api.patch(
                `/admin/associations/${selectedAid}/assignments`,
                body,
            );
            const aname =
                assos.find((a) => a.association_id === selectedAid)?.name ||
                "asso";
            toast.success(
                mode === "add"
                    ? `${userIds.length} recruteur·s assigné·s à ${aname} (total: ${r.data.assigned_count})`
                    : `${userIds.length} recruteur·s retiré·s de ${aname}`,
            );
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur lors de l'assignation");
        } finally {
            setBusy(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="assign-mission-modal"
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="glass-card rounded-3xl p-6 max-w-md w-full"
            >
                <div className="flex items-center gap-2.5 mb-4">
                    <div className="w-10 h-10 rounded-2xl bg-shariti-orange/15 flex items-center justify-center">
                        <Target className="w-5 h-5 text-shariti-orange-light" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-orange-light">
                            Action en masse
                        </p>
                        <h3 className="text-lg font-extrabold text-white tracking-tight">
                            Assigner {userIds.length} recruteur·s à une mission
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
                    >
                        <X className="w-4 h-4 text-white/60" />
                    </button>
                </div>

                <p className="text-xs text-white/55 mb-4 leading-relaxed">
                    Choisis l'association sur laquelle tu veux positionner les recruteurs
                    sélectionnés. Cela leur attribuera le badge{" "}
                    <span className="font-bold text-emerald-300">TON ASSO</span> sur leur profil et
                    leur dashboard.
                </p>

                <div className="space-y-3">
                    {/* Mode toggle */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => setMode("add")}
                            data-testid="assign-mission-mode-add"
                            className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                                mode === "add"
                                    ? "bg-shariti-orange text-white"
                                    : "bg-white/5 text-white/60 hover:bg-white/10"
                            }`}
                        >
                            <UserPlus className="w-4 h-4" />
                            Assigner
                        </button>
                        <button
                            onClick={() => setMode("remove")}
                            data-testid="assign-mission-mode-remove"
                            className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                                mode === "remove"
                                    ? "bg-red-500/30 text-red-200 border border-red-500/40"
                                    : "bg-white/5 text-white/60 hover:bg-white/10"
                            }`}
                        >
                            <UserMinus className="w-4 h-4" />
                            Retirer
                        </button>
                    </div>

                    {/* Asso picker */}
                    <div>
                        <label className="text-[10px] font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                            Association
                        </label>
                        {assos === null ? (
                            <div className="h-12 rounded-xl bg-white/5 animate-pulse" />
                        ) : (
                            <ThemedSelect
                                value={selectedAid}
                                onChange={setSelectedAid}
                                options={assos.map((a) => ({
                                    value: a.association_id,
                                    label: `${a.name} (${a.assigned_count || 0} assigné·s)`,
                                }))}
                                dataTestid="assign-mission-asso-select"
                            />
                        )}
                    </div>
                </div>

                <div className="mt-6 flex gap-2">
                    <button
                        onClick={onClose}
                        className="px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white/70 text-sm font-bold"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={submit}
                        disabled={busy || !selectedAid || userIds.length === 0}
                        data-testid="assign-mission-submit"
                        className="flex-1 px-4 py-2.5 rounded-xl bg-shariti-orange hover:bg-shariti-orange-light text-white font-extrabold text-sm orange-glow flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                        {mode === "add" ? (
                            <>
                                <UserPlus className="w-4 h-4" />
                                Assigner {userIds.length}
                            </>
                        ) : (
                            <>
                                <UserMinus className="w-4 h-4" />
                                Retirer {userIds.length}
                            </>
                        )}
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
}
